<template>
	<div class="comment-form elevation-0 pl-3 py-3">
		<v-form
			style="width: 100%"
			ref="form"
			v-model="valid"
			lazy-validation
			@submit.prevent="validateAndSubmit"
		>
			<div class="d-content comment-form__text-wrapper">
				<Avatar
					class="comment-form__avatar mr-2"
					:size="40"
					:avatar="this.profile.user_logo"
				/>

				<div class="comment-form__form-area">
					<div
						ref="editableDiv"
						class="comment-form__editable"
						:class="
							fields.content && fields.content.trim()
								? 'comment-form__editable--active'
								: ''
						"
						contenteditable="true"
						data-placeholder="Your comment ..."
						@input="onEditableInput"
					></div>

					<button
						:style="{
							backgroundColor: isSubmitDisabled ? '#9b9b9b' : 'var(--primary_color)',
						}"
						type="submit"
						class="comment-form__button elevation-0"
						:disabled="replyLoading || isSubmitDisabled"
					>
						<v-progress-circular v-if="replyLoading"></v-progress-circular>
						<span v-else> Comment </span>
					</button>
				</div>
			</div>

			<p v-for="(index, error) in errors" :key="index" class="red--text">
				{{ error }}
			</p>
		</v-form>

		<CommentMentionBar
			v-if="isMentionActive"
			:searchString="activeMentionSearch"
			:mentions="mentions"
			@setMention="setMention"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'
import Avatar from '@/components/Avatar'
import CommentMentionBar from '@/components/CommentMentionBar.vue'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'CommentForm',

	components: {
		CommentMentionBar,
		Avatar,
	},

	mixins: [Form, Dwh],

	props: {
		isReply: {
			type: Boolean,
			default: false,
		},
		parent: {
			type: Object,
			default: () => ({}),
		},
		post: {
			type: Object,
			default: () => ({}),
		},
		isEdit: {
			type: Boolean,
			default: false,
		},
		comment: {
			type: Object,
			default: () => ({}),
		},
		autoMentionUser: {
			type: Object,
			default: null,
		},
		forum: {
			type: Object,
			default: () => ({}),
		},
		isPostComment: {
			type: Boolean,
			default: false,
		},
	},

	data: () => ({
		fields: {
			content: '',
		},
		mentions: [],
	}),

	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('post', ['replyLoading']),
		activeMentionSearch() {
			const range = this.getActiveMentionRange()
			return range ? range.searchText : ''
		},

		isSubmitDisabled() {
			return !this.fields.content || !this.fields.content.trim()
		},

		isMentionActive() {
			if (!this.fields.content || this.isCommentAdding || this.isForumCommentAdding) {
				return false
			}

			return !!this.getActiveMentionRange()
		},

		rules() {
			return {
				content: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => !!v.trim().length || 'Text cannot be empty',
				],
			}
		},
	},

	watch: {
		'fields.content': {
			handler(newVal) {
				if (!newVal) {
					this.mentions = []
					return
				}

				this.mentions = this.mentions.filter((mention) => {
					const normalizedUsername = mention.visible_username.replace(/\s+/g, '')

					const regex = new RegExp(
						`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
						'g'
					)

					return regex.test(newVal)
				})
			},
		},
	},

	mounted() {
		if (this.autoMentionUser && this.fields.content === '') {
			const username = this.autoMentionUser.visible_username.replace(/\s+/g, '')

			this.fields.content = `@${username} `
			this.mentions.push(this.autoMentionUser)
		}

		this.syncDivFromContent()
	},

	methods: {
		...mapActions('post', ['addReply']),
		...mapActions('forum', ['setActiveComment']),
		getActiveMentionRange() {
			const content = this.fields.content || ''

			const match = content.match(/(^|\s)@([^\s@]*)$/)

			if (!match) return null

			const prefixLength = match[1].length
			const atIndex = match.index + prefixLength

			return {
				atIndex,
				searchText: match[2],
			}
		},

		onEditableInput(e) {
			const text = e.target.innerText.replace(/\n/g, '').trim()

			this.fields.content = text ? e.target.innerText : ''

			if (!text) {
				e.target.innerHTML = ''
			}
		},

		setMention(user) {
			const activeMention = this.getActiveMentionRange()

			if (!activeMention) return

			const normalizedUsername = user.visible_username.replace(/\s+/g, '')

			const before = this.fields.content.slice(0, activeMention.atIndex)

			this.fields.content = `${before}@${normalizedUsername} `

			this.mentions.push(user)

			this.$nextTick(() => {
				this.syncDivFromContent()
				this.setCursorToEnd(this.$refs.editableDiv)
			})
		},

		submitForm() {
			if (this.isSubmitDisabled) {
				return
			}

			this.field_errors = {}

			let result = this.fields.content

			this.mentions.forEach((item) => {
				const normalizedUsername = item.visible_username.replace(/\s+/g, '')
				const regex = new RegExp(
					`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
					'g'
				)
				result = result.replace(regex, `$1@[${item.user_id}|${item.visible_username}]`)
			})

			const {
				comment: { id: parentCommentId },
				post: { thread_id },
			} = this.$props
			if (!this.isPostComment) {
				this.sendTrigger({
					category: 'Discussion Post',
					action: 'click_send_reply_forum_post',
					module: 'Forums / Communities',
					title: this.forum.thread_title,
					id1: this.forum.thread_id,
				})
			}

			return this.addReply({
				thread_id,
				reply_text: result,
				parent_id: parentCommentId,
				isPost: this.$route.name === 'posts-by-id',
				thread_title: this.post.thread_title,
			})
				.then(() => {
					this.fields.content = ''
					this.mentions = []

					if (this.$refs.editableDiv) {
						this.$refs.editableDiv.innerHTML = ''
					}

					this.setActiveComment(false)

					this.$nextTick(() => {
						this.syncDivFromContent()
					})
				})
				.catch((errors) => {
					this.field_errors = errors
				})
		},

		renderHTML(text) {
			if (!text) return ''

			const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

			let result = escaped

			this.mentions.forEach((mention) => {
				const normalizedUsername = mention.visible_username.replace(/\s+/g, '')

				const regex = new RegExp(
					`@(${this.escapeRegex(normalizedUsername)})(?=\\s|$|&)`,
					'g'
				)

				result = result.replace(
					regex,
					`<span class="mention-chip" contenteditable="false">@$1</span>`
				)
			})

			return result
		},

		escapeRegex(str) {
			return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		},

		syncDivFromContent() {
			const div = this.$refs.editableDiv

			if (!div) return

			div.innerHTML = this.renderHTML(this.fields.content)
		},

		setCursorToEnd(el) {
			if (!el) return

			const range = document.createRange()
			const sel = window.getSelection()

			range.selectNodeContents(el)
			range.collapse(false)

			sel.removeAllRanges()
			sel.addRange(range)

			el.focus()
		},
	},
}
</script>

<style scoped lang="scss">
.comment-form {
	min-height: 65px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-family: 'Inter', sans-serif;
	font-style: normal;
	background-color: #ffffff !important;
	font-weight: 500;
	font-size: 20px;
	line-height: 24px;
	letter-spacing: 0.5px;
	position: relative;

	&__text-wrapper {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	&__avatar {
		border-radius: 8px;
	}

	&__form-area {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-right: 12px;
		gap: 16px;
	}

	&__editable {
		width: 100%;
		max-width: 331px;
		min-height: 48px;
		padding: 12px;
		border-radius: 8px;
		background: #ffffff;
		outline: none;
		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 16px;
		line-height: 140%;
		color: #4a4a4a;
		white-space: pre-wrap;
		word-break: break-all;
		cursor: text;
		transition: border-color 0.2s ease;

		&:empty::before {
			content: attr(data-placeholder);
			color: #9b9b9b;
			pointer-events: none;
			font-weight: 400;
		}
	}

	&__button {
		width: 134px;
		height: 46px;
		display: flex;
		flex-shrink: 0;
		border-radius: 8px;
		align-items: center;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		justify-content: center;
		color: #ffffff;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.7;
		}
	}
}

.mention-chip {
	display: inline-block;
	color: var(--primary_color);
	border-radius: 4px;
	font-weight: 500;
	font-size: inherit;
	line-height: inherit;
	cursor: default;
	user-select: none;
}

.comment-form .mention-bar {
	position: absolute;
	bottom: 100%;
	z-index: 10;
}
</style>
